import React from 'react';
import { useLanguage } from '../lib/LanguageContext';
import { integratedLanguageService } from '../services/integratedLanguageService';
// Game data is now loaded from CDN, no need for gameDataService

const LanguageTest: React.FC = () => {
  const { currentLanguage, setLanguage } = useLanguage();

  const handleLanguageChange = async (newLanguage: string) => {
    console.log('🔄 Testing language change to', newLanguage);
    console.log('Before change - i18n.language:', (await import('../lib/i18n')).default.language);
    console.log('Before change - currentLanguage:', currentLanguage);
    
    try {
      await setLanguage(newLanguage);
      console.log('After change - i18n.language:', (await import('../lib/i18n')).default.language);
      console.log('After change - currentLanguage:', currentLanguage);
      console.log('Test translation:', (await import('../lib/i18n')).default.t('app.title'));
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const testGameDataLoading = async () => {
    console.log('🎮 Testing game data loading...');
    console.log('✅ Game data is now loaded from CDN by GameQuestionManager');
    console.log('No need to test gameDataService as it has been removed');
  };

  const testIntegratedService = async () => {
    console.log('🔄 Testing integrated language service...');
    try {
      await integratedLanguageService.loadGameData('wouldYouRather', currentLanguage);
      console.log('✅ Integrated service test successful');
    } catch (error) {
      console.error('❌ Error with integrated service:', error);
    }
  };

  const debugTranslations = async () => {
    console.log('🔍 Debugging translations...');
    const i18n = (await import('../lib/i18n')).default;
    
    console.log('Current language:', i18n.language);
    console.log('Available languages:', i18n.languages);
    console.log('Has resource bundles:');
    console.log('- hi:', i18n.hasResourceBundle('hi', 'translation'));
    console.log('- es:', i18n.hasResourceBundle('es', 'translation'));
    console.log('- fr:', i18n.hasResourceBundle('fr', 'translation'));
    console.log('- en:', i18n.hasResourceBundle('en', 'translation'));
    
    console.log('Translation tests:');
    console.log('- app.title:', i18n.t('app.title'));
    console.log('- header.title:', i18n.t('header.title'));
    
    // Test all languages
    const testLanguages = ['en', 'es', 'fr', 'hi'];
    for (const lang of testLanguages) {
      if (i18n.hasResourceBundle(lang, 'translation')) {
        console.log(`${lang} app.title:`, i18n.t('app.title', { lng: lang }));
      } else {
        console.log(`${lang}: Not loaded`);
      }
    }
  };

  const testLazyLoading = async () => {
    console.log('🧪 Testing lazy loading strategy for 24+ languages...');
    
    // Test 1: Check initial state
    console.log('📊 Initial cache state:', integratedLanguageService.getCurrentCacheStatus());
    
    // Test 2: Load a new language on demand
    console.log('🔄 Loading German (de) on demand...');
    await integratedLanguageService.changeLanguage('de');
    console.log('✅ German loaded and cached');
    
    // Test 3: Load another language
    console.log('🔄 Loading Italian (it) on demand...');
    await integratedLanguageService.changeLanguage('it');
    console.log('✅ Italian loaded and cached');
    
    // Test 4: Check cache management
    console.log('📊 Cache after loading 2 new languages:', integratedLanguageService.getCurrentCacheStatus());
    
    // Test 5: Load game data on demand
    console.log('🎮 Loading game data for Italian...');
    await integratedLanguageService.loadGameData('wouldYouRather', 'it');
    console.log('✅ Game data loaded for Italian');
    
    // Test 6: Switch back to English
    console.log('🔄 Switching back to English...');
    await integratedLanguageService.changeLanguage('en');
    console.log('✅ Back to English');
    
    // Test 7: Final cache state
    console.log('📊 Final cache state:', integratedLanguageService.getCurrentCacheStatus());
    
    console.log('✅ Lazy loading test completed');
  };

  const testMemoryUsage = () => {
    console.log('🧪 Testing memory usage...');
    
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      console.log('📊 Memory Usage:');
      console.log(`  Used: ${Math.round(memory.usedJSHeapSize / 1024 / 1024)}MB`);
      console.log(`  Total: ${Math.round(memory.totalJSHeapSize / 1024 / 1024)}MB`);
      console.log(`  Limit: ${Math.round(memory.jsHeapSizeLimit / 1024 / 1024)}MB`);
    }
    
    console.log('📊 Cache Info:', integratedLanguageService.getCacheStats());
  };

  const testInitialLanguageLoading = () => {
    console.log('🧪 Testing initial language loading...');
    
    // Check what languages are loaded initially
    const i18n = (window as any).i18next;
    const loadedLanguages = Object.keys(i18n.store.data || {});
    
    console.log('📊 Initial loaded languages:', loadedLanguages);
    console.log('📊 Current i18n language:', i18n.language);
    console.log('📊 Current context language:', currentLanguage);
    
    // Check which translation files are available
    const availableTranslations = ['en', 'es', 'fr', 'hi', 'de', 'it', 'pt', 'ru', 'ja', 'ko', 'zh', 'ar'];
    const loadedTranslations = availableTranslations.filter(lang => 
      i18n.hasResourceBundle(lang, 'translation')
    );
    
    console.log('📊 Available translation files:', availableTranslations);
    console.log('📊 Actually loaded translations:', loadedTranslations);
    console.log('📊 Not yet loaded:', availableTranslations.filter(lang => !loadedTranslations.includes(lang)));
    
    // Check integrated service cache
    console.log('📊 Integrated service cache:', integratedLanguageService.getCurrentCacheStatus());
    
    return {
      loadedLanguages,
      currentLanguage: i18n.language,
      loadedTranslations,
      availableTranslations
    };
  };

  const testLoadUnloadedLanguage = async (languageCode: string) => {
    console.log(`🧪 Testing loading of unloaded language: ${languageCode}`);
    
    // Check if language is already loaded
    const i18n = (window as any).i18next;
    const isAlreadyLoaded = i18n.hasResourceBundle(languageCode, 'translation');
    
    console.log(`📊 Is ${languageCode} already loaded?`, isAlreadyLoaded);
    
    if (isAlreadyLoaded) {
      console.log(`⚠️ ${languageCode} is already loaded, testing switch instead`);
      await handleLanguageChange(languageCode);
      return;
    }
    
    // Test loading the unloaded language
    console.log(`🔄 Loading ${languageCode} for the first time...`);
    const startTime = performance.now();
    
    try {
      await integratedLanguageService.changeLanguage(languageCode);
      const endTime = performance.now();
      const loadTime = endTime - startTime;
      
      console.log(`✅ Successfully loaded ${languageCode} in ${loadTime.toFixed(2)}ms`);
      console.log(`📊 Translation test:`, i18n.t('app.title'));
      
      // Check if it's now loaded
      const isNowLoaded = i18n.hasResourceBundle(languageCode, 'translation');
      console.log(`📊 Is ${languageCode} now loaded?`, isNowLoaded);
      
      // Check cache status
      console.log('📊 Updated cache status:', integratedLanguageService.getCurrentCacheStatus());
      
    } catch (error) {
      console.error(`❌ Failed to load ${languageCode}:`, error);
    }
  };

  const testLoadMultipleUnloadedLanguages = async () => {
    console.log('🧪 Testing loading multiple unloaded languages...');
    
    const languagesToTest = ['de', 'it', 'pt', 'ru', 'ja']; // Languages likely not loaded initially
    const i18n = (window as any).i18next;
    
    for (const lang of languagesToTest) {
      const isLoaded = i18n.hasResourceBundle(lang, 'translation');
      console.log(`📊 ${lang}: ${isLoaded ? '✅ Loaded' : '❌ Not loaded'}`);
      
      if (!isLoaded) {
        console.log(`🔄 Loading ${lang}...`);
        const startTime = performance.now();
        
        try {
          await integratedLanguageService.changeLanguage(lang);
          const endTime = performance.now();
          console.log(`✅ ${lang} loaded in ${(endTime - startTime).toFixed(2)}ms`);
          console.log(`📊 Translation:`, i18n.t('app.title'));
        } catch (error) {
          console.error(`❌ Failed to load ${lang}:`, error);
        }
        
        // Small delay between loads
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    console.log('📊 Final cache status:', integratedLanguageService.getCurrentCacheStatus());
  };

  const testCacheEviction = async () => {
    console.log('🧪 Testing cache eviction with multiple languages...');
    
    const languagesToTest = ['de', 'it', 'pt', 'ru', 'ja', 'ko', 'zh', 'ar'];
    const i18n = (window as any).i18next;
    
    console.log('📊 Initial cache status:', integratedLanguageService.getCurrentCacheStatus());
    
    // Load multiple languages to trigger cache eviction
    for (const lang of languagesToTest) {
      console.log(`🔄 Loading ${lang}...`);
      try {
        await integratedLanguageService.changeLanguage(lang);
        console.log(`✅ ${lang} loaded`);
        console.log(`📊 Translation:`, i18n.t('app.title'));
        
        // Check cache after each load
        console.log('📊 Cache status:', integratedLanguageService.getCurrentCacheStatus());
        
      } catch (error) {
        console.error(`❌ Failed to load ${lang}:`, error);
      }
      
      // Small delay
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    console.log('📊 Final cache status after eviction:', integratedLanguageService.getCurrentCacheStatus());
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '4px',
      fontSize: '11px'
    }}>
      <div style={{ marginBottom: '4px', color: '#FFD700' }}>
        Current: {currentLanguage}
      </div>
      
      <div style={{ display: 'flex', gap: '2px', marginBottom: '4px' }}>
        <button onClick={() => handleLanguageChange('en')} style={{ fontSize: '10px', padding: '2px 4px', background: '#4CAF50' }}>
          Test EN
        </button>
        <button onClick={() => handleLanguageChange('es')} style={{ fontSize: '10px', padding: '2px 4px', background: '#4CAF50' }}>
          Test ES
        </button>
        <button onClick={() => handleLanguageChange('fr')} style={{ fontSize: '10px', padding: '2px 4px', background: '#4CAF50' }}>
          Test FR
        </button>
        <button onClick={() => handleLanguageChange('hi')} style={{ fontSize: '10px', padding: '2px 4px', background: '#4CAF50' }}>
          Test HI
        </button>
      </div>

      <div style={{ display: 'flex', gap: '2px', marginBottom: '4px' }}>
        <button onClick={testGameDataLoading} style={{ fontSize: '10px', padding: '2px 4px', background: '#FF5722' }}>
          Test Game Data
        </button>
        <button onClick={testIntegratedService} style={{ fontSize: '10px', padding: '2px 4px', background: '#2196F3' }}>
          Test Integrated
        </button>
        <button onClick={debugTranslations} style={{ fontSize: '10px', padding: '2px 4px', background: '#FF9800' }}>
          Debug Translations
        </button>
      </div>

      <div style={{ display: 'flex', gap: '2px', marginBottom: '4px' }}>
        <button onClick={testLazyLoading} style={{ fontSize: '10px', padding: '2px 4px', background: '#9C27B0' }}>
          Test Lazy Loading
        </button>
        <button onClick={testMemoryUsage} style={{ fontSize: '10px', padding: '2px 4px', background: '#E91E63' }}>
          Test Memory Usage
        </button>
        <button onClick={testInitialLanguageLoading} style={{ fontSize: '10px', padding: '2px 4px', background: '#607D8B' }}>
          Check Initial Loading
        </button>
      </div>

      <div style={{ display: 'flex', gap: '2px', marginBottom: '4px' }}>
        <button onClick={() => testLoadUnloadedLanguage('de')} style={{ fontSize: '10px', padding: '2px 4px', background: '#795548' }}>
          Load German
        </button>
        <button onClick={() => testLoadUnloadedLanguage('it')} style={{ fontSize: '10px', padding: '2px 4px', background: '#795548' }}>
          Load Italian
        </button>
        <button onClick={testLoadMultipleUnloadedLanguages} style={{ fontSize: '10px', padding: '2px 4px', background: '#FF5722' }}>
          Load Multiple
        </button>
      </div>

      <div style={{ display: 'flex', gap: '2px' }}>
        <button onClick={testCacheEviction} style={{ fontSize: '10px', padding: '2px 4px', background: '#3F51B5' }}>
          Test Cache Eviction
        </button>
      </div>
    </div>
  );
};

export default LanguageTest; 